/**
 * Bespoke d3 chart primitives for the Analysis > Biomarkers & Genomics screen.
 *
 * React-driven SVG (d3 for scales/bins/line math only), sized with a fixed
 * viewBox + width:100% so each card scales to its container. Colours and the
 * "Work Sans" type match the house categorical palette (see
 * components/CategoricalPlots/barplot.tsx).
 */
import * as d3 from "d3";

// House categorical palette (barplot.tsx) + neutral axis colours (XYLinePlot.tsx).
export const SERIES = [
  "#64BDE1",
  "#7DC199",
  "#C275A1",
  "#FBD560",
  "#E5AB5F",
  "#BEBEBE",
];
export const PRIMARY = "#64BDE1";
const AXIS = "#9ca3af";
const TICK = "#6b7280";
const LABEL = "#374151";
const GRID = "#e5e7eb";
const FONT = "Work Sans";

/** ctDNA molecular-response colour map (green improve / blue stable / magenta worse). */
export const MOLRESP_COLORS: Record<string, string> = {
  "Molecular response": "#5BB27F",
  "No change": "#64BDE1",
  "Molecular progression": "#C275A1",
  "Not evaluable": "#BEBEBE",
};
export const molrespColor = (g: string | null | undefined): string =>
  (g && MOLRESP_COLORS[g]) || "#BEBEBE";

const W = 380;
const H = 260;
const M = { top: 16, right: 16, bottom: 46, left: 48 };
const IW = W - M.left - M.right;
const IH = H - M.top - M.bottom;

function EmptyNote({ label }: { label: string }) {
  return (
    <div className="flex items-center justify-center h-[200px] text-[13px] text-[#8C8C8C] font-work-sans">
      No {label} data
    </div>
  );
}

function svgProps(className = "") {
  return {
    viewBox: `0 0 ${W} ${H}`,
    preserveAspectRatio: "xMidYMid meet" as const,
    width: "100%",
    className,
    style: { fontFamily: FONT },
  };
}

// --------------------------------------------------------------------------- //
// Histogram — distribution of a numeric analyte.
// --------------------------------------------------------------------------- //
export function Histogram({
  values,
  xLabel,
  color = PRIMARY,
}: {
  values: number[];
  xLabel: string;
  color?: string;
}) {
  const clean = values.filter((v) => Number.isFinite(v));
  if (clean.length === 0) return <EmptyNote label={xLabel} />;

  const x = d3
    .scaleLinear()
    .domain([Math.min(0, d3.min(clean)!), d3.max(clean)!])
    .nice();
  const bins = d3
    .bin()
    .domain(x.domain() as [number, number])
    .thresholds(12)(clean);
  const xr = x.range([M.left, M.left + IW]);
  const y = d3
    .scaleLinear()
    .domain([0, d3.max(bins, (b) => b.length) || 1])
    .nice()
    .range([M.top + IH, M.top]);

  return (
    <svg {...svgProps()}>
      {y.ticks(4).map((t) => (
        <g key={`y${t}`}>
          <line
            x1={M.left}
            x2={M.left + IW}
            y1={y(t)}
            y2={y(t)}
            stroke={GRID}
          />
          <text
            x={M.left - 6}
            y={y(t)}
            dy="0.32em"
            textAnchor="end"
            fontSize={10}
            fill={TICK}
          >
            {t}
          </text>
        </g>
      ))}
      {bins.map((b, i) => {
        const x0 = xr(b.x0!);
        const x1 = xr(b.x1!);
        return (
          <rect
            key={i}
            x={x0 + 0.5}
            width={Math.max(0, x1 - x0 - 1)}
            y={y(b.length)}
            height={M.top + IH - y(b.length)}
            fill={color}
            opacity={0.85}
          />
        );
      })}
      <line
        x1={M.left}
        x2={M.left + IW}
        y1={M.top + IH}
        y2={M.top + IH}
        stroke={AXIS}
      />
      {xr.ticks(6).map((t) => (
        <text
          key={`x${t}`}
          x={xr(t)}
          y={M.top + IH + 16}
          textAnchor="middle"
          fontSize={10}
          fill={TICK}
        >
          {t}
        </text>
      ))}
      <text
        x={M.left + IW / 2}
        y={H - 8}
        textAnchor="middle"
        fontSize={11}
        fill={LABEL}
      >
        {xLabel}
      </text>
    </svg>
  );
}

// --------------------------------------------------------------------------- //
// CountBar — vertical bars of category counts (e.g. MSI status).
// --------------------------------------------------------------------------- //
export function CountBar({
  data,
  colors = SERIES,
}: {
  data: { label: string; count: number }[];
  colors?: string[];
}) {
  if (data.length === 0) return <EmptyNote label="MSI" />;
  const x = d3
    .scaleBand()
    .domain(data.map((d) => d.label))
    .range([M.left, M.left + IW])
    .padding(0.4);
  const y = d3
    .scaleLinear()
    .domain([0, d3.max(data, (d) => d.count) || 1])
    .nice()
    .range([M.top + IH, M.top]);
  return (
    <svg {...svgProps()}>
      {y.ticks(4).map((t) => (
        <line
          key={t}
          x1={M.left}
          x2={M.left + IW}
          y1={y(t)}
          y2={y(t)}
          stroke={GRID}
        />
      ))}
      {data.map((d, i) => (
        <g key={d.label}>
          <rect
            x={x(d.label)}
            width={x.bandwidth()}
            y={y(d.count)}
            height={M.top + IH - y(d.count)}
            fill={colors[i % colors.length]}
          />
          <text
            x={x(d.label)! + x.bandwidth() / 2}
            y={y(d.count) - 4}
            textAnchor="middle"
            fontSize={11}
            fill={LABEL}
          >
            {d.count}
          </text>
          <text
            x={x(d.label)! + x.bandwidth() / 2}
            y={M.top + IH + 16}
            textAnchor="middle"
            fontSize={11}
            fill={TICK}
          >
            {d.label}
          </text>
        </g>
      ))}
      <line
        x1={M.left}
        x2={M.left + IW}
        y1={M.top + IH}
        y2={M.top + IH}
        stroke={AXIS}
      />
    </svg>
  );
}

// --------------------------------------------------------------------------- //
// FreqBars — horizontal frequency bars (oncoprint-lite gene frequency).
// --------------------------------------------------------------------------- //
export function FreqBars({
  data,
  color = "#7DC199",
}: {
  data: { label: string; count: number }[];
  color?: string;
}) {
  if (data.length === 0) return <EmptyNote label="alteration" />;
  const rowH = 18;
  const height = M.top + data.length * rowH + 24;
  const left = 92;
  const iw = W - left - M.right;
  const x = d3
    .scaleLinear()
    .domain([0, d3.max(data, (d) => d.count) || 1])
    .range([left, left + iw]);
  return (
    <svg
      viewBox={`0 0 ${W} ${height}`}
      preserveAspectRatio="xMidYMid meet"
      width="100%"
      style={{ fontFamily: FONT }}
    >
      {data.map((d, i) => {
        const yy = M.top + i * rowH;
        return (
          <g key={d.label}>
            <text
              x={left - 6}
              y={yy + rowH / 2}
              dy="0.32em"
              textAnchor="end"
              fontSize={11}
              fill={LABEL}
            >
              {d.label}
            </text>
            <rect
              x={left}
              y={yy + 2}
              width={Math.max(1, x(d.count) - left)}
              height={rowH - 5}
              fill={color}
              rx={2}
            />
            <text
              x={x(d.count) + 4}
              y={yy + rowH / 2}
              dy="0.32em"
              fontSize={10}
              fill={TICK}
            >
              {d.count}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

// --------------------------------------------------------------------------- //
// TmbResponseScatter — TMB (x) by molecular-response category (banded y, jitter).
// --------------------------------------------------------------------------- //
export function TmbResponseScatter({
  points,
  groups,
}: {
  points: { x: number; group: string }[];
  groups: string[];
}) {
  const clean = points.filter((p) => Number.isFinite(p.x));
  if (clean.length === 0) return <EmptyNote label="TMB" />;
  const left = 132;
  const iw = W - left - M.right;
  const x = d3
    .scaleLinear()
    .domain([0, d3.max(clean, (p) => p.x)!])
    .nice()
    .range([left, left + iw]);
  const y = d3
    .scaleBand()
    .domain(groups)
    .range([M.top, M.top + IH])
    .padding(0.3);
  // Deterministic vertical jitter within each band (index-based, no randomness).
  const perGroup: Record<string, number> = {};
  return (
    <svg {...svgProps()}>
      {groups.map((g) => (
        <g key={g}>
          <line
            x1={left}
            x2={left + iw}
            y1={y(g)! + y.bandwidth() / 2}
            y2={y(g)! + y.bandwidth() / 2}
            stroke={GRID}
          />
          <text
            x={left - 6}
            y={y(g)! + y.bandwidth() / 2}
            dy="0.32em"
            textAnchor="end"
            fontSize={10}
            fill={TICK}
          >
            {g}
          </text>
        </g>
      ))}
      {clean.map((p, i) => {
        const n = (perGroup[p.group] = (perGroup[p.group] ?? 0) + 1);
        const band = y(p.group);
        if (band === undefined) return null;
        const jitter = ((n % 7) - 3) * (y.bandwidth() / 16);
        return (
          <circle
            key={i}
            cx={x(p.x)}
            cy={band + y.bandwidth() / 2 + jitter}
            r={4}
            fill={molrespColor(p.group)}
            opacity={0.8}
          />
        );
      })}
      <line
        x1={left}
        x2={left + iw}
        y1={M.top + IH}
        y2={M.top + IH}
        stroke={AXIS}
      />
      {x.ticks(6).map((t) => (
        <text
          key={t}
          x={x(t)}
          y={M.top + IH + 16}
          textAnchor="middle"
          fontSize={10}
          fill={TICK}
        >
          {t}
        </text>
      ))}
      <text
        x={left + iw / 2}
        y={H - 8}
        textAnchor="middle"
        fontSize={11}
        fill={LABEL}
      >
        Tumor Mutational Burden (mut/Mb)
      </text>
    </svg>
  );
}

// --------------------------------------------------------------------------- //
// CtdnaSlope — paired baseline -> Cycle 3 ctDNA VAF, one line per subject,
// coloured by molecular response.
// --------------------------------------------------------------------------- //
export function CtdnaSlope({
  series,
}: {
  series: {
    subject: string;
    baseline: number;
    cycle3: number;
    group: string;
  }[];
}) {
  const clean = series.filter(
    (s) => Number.isFinite(s.baseline) && Number.isFinite(s.cycle3),
  );
  if (clean.length === 0) return <EmptyNote label="ctDNA" />;
  // Wide banner aspect: a broad viewBox so a full-width card renders the chart at
  // roughly 1:1 (normal-sized marks/labels) instead of scaling a small viewBox up.
  const WIDE = 1040;
  const height = 300;
  const iw = WIDE - M.left - M.right;
  const ih = height - M.top - M.bottom;
  const maxV = d3.max(clean, (s) => Math.max(s.baseline, s.cycle3)) || 1;
  const y = d3
    .scaleLinear()
    .domain([0, maxV])
    .nice()
    .range([M.top + ih, M.top]);
  const xBL = M.left + iw * 0.33;
  const xC3 = M.left + iw * 0.67;
  return (
    <svg
      viewBox={`0 0 ${WIDE} ${height}`}
      preserveAspectRatio="xMidYMid meet"
      width="100%"
      style={{ fontFamily: FONT, maxHeight: 320 }}
    >
      {y.ticks(5).map((t) => (
        <g key={t}>
          <line
            x1={M.left}
            x2={M.left + iw}
            y1={y(t)}
            y2={y(t)}
            stroke={GRID}
          />
          <text
            x={M.left - 6}
            y={y(t)}
            dy="0.32em"
            textAnchor="end"
            fontSize={10}
            fill={TICK}
          >
            {t}
          </text>
        </g>
      ))}
      {/* vertical reference lines at the two timepoints */}
      <line x1={xBL} x2={xBL} y1={M.top} y2={M.top + ih} stroke={GRID} />
      <line x1={xC3} x2={xC3} y1={M.top} y2={M.top + ih} stroke={GRID} />
      {clean.map((s, i) => (
        <g key={i} opacity={0.75}>
          <line
            x1={xBL}
            y1={y(s.baseline)}
            x2={xC3}
            y2={y(s.cycle3)}
            stroke={molrespColor(s.group)}
            strokeWidth={1.3}
          />
          <circle
            cx={xBL}
            cy={y(s.baseline)}
            r={3}
            fill={molrespColor(s.group)}
          />
          <circle
            cx={xC3}
            cy={y(s.cycle3)}
            r={3}
            fill={molrespColor(s.group)}
          />
        </g>
      ))}
      <line
        x1={M.left}
        x2={M.left + iw}
        y1={M.top + ih}
        y2={M.top + ih}
        stroke={AXIS}
      />
      <text
        x={xBL}
        y={M.top + ih + 18}
        textAnchor="middle"
        fontSize={11}
        fill={LABEL}
      >
        Baseline
      </text>
      <text
        x={xC3}
        y={M.top + ih + 18}
        textAnchor="middle"
        fontSize={11}
        fill={LABEL}
      >
        Cycle 3
      </text>
      <text
        x={14}
        y={M.top + ih / 2}
        textAnchor="middle"
        fontSize={11}
        fill={LABEL}
        transform={`rotate(-90 14 ${M.top + ih / 2})`}
      >
        ctDNA VAF (%)
      </text>
    </svg>
  );
}

/** Small legend row for the molecular-response colour groups. */
export function MolRespLegend({ groups }: { groups: string[] }) {
  return (
    <div className="flex flex-wrap gap-3 mt-2">
      {groups.map((g) => (
        <div
          key={g}
          className="flex items-center gap-1 text-[11px] text-[#556C75] font-work-sans"
        >
          <span
            className="inline-block w-3 h-3 rounded-sm"
            style={{ background: molrespColor(g) }}
          />
          {g}
        </div>
      ))}
    </div>
  );
}
